// ---------------------------------------------------------------------------
// Dashboard "Vendas a prazo e adimplência" — constantes do recorte (0084).
//
// Tudo que é opção de filtro/lente mora aqui: os presets de período, a lente de
// vencimento, os horizontes do forecast e os meses/limiar da safra. As chaves
// dos params são as do backend (snake_case) — a tradução camel→snake acontece
// no service, não na tela.
// ---------------------------------------------------------------------------

// Token do contrato para "registros SEM classificação" (`IS NULL`), aceito
// dentro do CSV de `origin_id` e `bank_account_id` — handoff 0086 §2.2.
//
// É `none` em inglês, como os outros params (`by`, `date_basis`, `limit`); o
// CAMPO de resposta correspondente é `sem_classificacao`, em português, como o
// resto do payload. A assimetria é deliberada e documentada no 0086 §2.3:
// mandar `origin_id=sem_classificacao` devolve 422.
export const UNCLASSIFIED_TOKEN = 'none';

// Início da cobertura confiável de ORIGEM. Antes disso, os títulos vieram da
// carga histórica do ASAAS, que não trouxe departamento gerador (~71% do valor
// da base). É a data que o banner de cobertura oferece como atalho.
export const ORIGIN_COVERAGE_START = '2026-03-01';

// Último mês da carga histórica — o `data_quality.historical_load_until` da API
// confirma isso a cada resposta; este valor é só o rótulo de fallback.
export const HISTORICAL_LOAD_UNTIL = '2026-02';

// ─── Período ──────────────────────────────────────────────────────────────
//
// ⚠️ Os presets são calculados a partir do `reference_date` que o SERVIDOR
// devolve — nunca de `new Date()` no cliente (o navegador pode estar em outro
// fuso; o domínio é America/Sao_Paulo e quem resolve "hoje" é o backend).
// Enquanto a referência não chegou, os chips ficam desabilitados. O default
// (`tudo`) não precisa de âncora nenhuma: é a ausência de `from`/`to`.
export const PERIOD_PRESETS = [
   { key: 'este_mes', label: 'Este mês' },
   { key: '90_dias', label: '90 dias' },
   { key: 'este_ano', label: 'Este ano' },
   { key: 'desde_mar_2026', label: 'Desde mar/2026' },
   { key: 'tudo', label: 'Tudo' },
];

export const DEFAULT_PERIOD = 'tudo';

// ─── Lente de vencimento (`date_basis`) ───────────────────────────────────
//
// Não é filtro, é lente: muda adimplência, aging e vintage ao mesmo tempo.
export const DATE_BASIS_OPTIONS = [
   {
      key: 'current',
      label: 'Vigente',
      pill: 'Vencimento vigente',
      hint: 'Considera prorrogações: "isto está vencido agora?" — visão de gestão do dia a dia.',
   },
   {
      key: 'original',
      label: 'Pactuado',
      pill: 'Vencimento pactuado',
      hint: 'Ignora prorrogações: "o cliente honrou o combinado?" — visão de rigor de crédito.',
   },
];

export const DEFAULT_DATE_BASIS = 'current';

// ─── Abas da tela ─────────────────────────────────────────────────────────
//
// O painel inteiro numa página só ficou pesado de renderizar: o heatmap de
// safra sozinho passa de 250 células, e há um SVG, cinco tabelas e dezenas de
// blocos com styled-components. Agrupar em abas permite MONTAR E DESMONTAR de
// verdade — não é esconder com CSS, que manteria o custo de layout e paint.
//
// O recorte (filtros) continua global e as 5 chamadas seguem em paralelo: a
// aba muda o que está na tela, não o que foi perguntado ao backend. Trocar de
// aba é instantâneo porque o dado já está em memória.
export const SECTION_TABS = [
   { key: 'portfolio', label: 'Saúde da carteira' },
   { key: 'breakdown', label: 'Onde nasce e concentra' },
   { key: 'vintage', label: 'Safra' },
];

export const DEFAULT_SECTION_TAB = 'portfolio';

// ─── Breakdown ────────────────────────────────────────────────────────────
export const BREAKDOWN_TABS = [
   { key: 'origin', label: 'Departamento origem' },
   { key: 'bank_account', label: 'CNPJ' },
   { key: 'customer', label: 'Cliente' },
];

export const DEFAULT_BREAKDOWN_BY = 'origin';
export const BREAKDOWN_LIMIT = 10;

// ─── Forecast / agenda de vencimentos ─────────────────────────────────────
// CUMULATIVOS a partir de hoje: o de 30 inclui o de 7.
export const FORECAST_HORIZONS = [7, 15, 30];

// Decomposição de cada horizonte (total = recebido + pendente).
//
// Azul escuro = dinheiro que já entrou (pagamento antecipado); azul de ação =
// o que ainda vai vencer. Mesma família porque nenhum dos dois é problema —
// o contraste é "já entrou" × "ainda vai vencer", não bom × ruim.
export const FORECAST_COLORS = {
   recebido: '#1f66bd',
   pendente: '#4098ef',
};

// ─── Safra (vintage) ──────────────────────────────────────────────────────
export const THRESHOLD_OPTIONS = [30, 60, 90];
export const DEFAULT_THRESHOLD_DIAS = 60;

// Meses (M+n) pedidos ao backend, POR RÉGUA de atraso.
//
// ⚠️ Não é preferência estética: os primeiros M+n são MATEMATICAMENTE zero para
// réguas altas. Um título emitido no mês M não tem como estar 60 dias vencido
// no fim de M+1 — a data não existe. O 0084 §4.5 mediu isso sobre as 48 safras
// reais: com régua de 60, M+1 é 0% em TODAS as safras, e o sinal começa em M+2.
//
// Plotar esse ponto ensinaria errado: o leitor veria toda safra começando
// impecável. Então cada régua pede o primeiro mês em que o atraso é possível
// (⌈régua/30⌉) e segue com os marcos de leitura.
export const VINTAGE_MONTHS_BY_THRESHOLD = {
   30: [1, 3, 6, 12],
   60: [2, 3, 6, 12],
   90: [3, 6, 9, 12],
};

export function vintageMonthsFor(thresholdDias) {
   return (
      VINTAGE_MONTHS_BY_THRESHOLD[thresholdDias] ??
      VINTAGE_MONTHS_BY_THRESHOLD[DEFAULT_THRESHOLD_DIAS]
   );
}

// Quantas safras entram no GRÁFICO de linhas (o heatmap abaixo mostra todas).
//
// A base tem 4 anos e meio de emissão: sem limite, o gráfico receberia dezenas
// de linhas e uma legenda impossível de usar. Seis é o teto da rampa ordinal
// abaixo — o número de degraus que ainda se distinguem.
export const VINTAGE_PLOT_LIMIT = 6;

// Cores das linhas de safra — rampa ORDINAL (safra é tempo, a ordem importa):
// um matiz, mais antiga clara → mais recente escura, terminando no azul escuro
// do handoff (#0b3a63).
//
// ⚠️ Diverge do handoff de design, que trazia 9 tons indo de cinza a azul
// (#9aa4ad…#0b3a63). Aquela sequência reprova na verificação: a luminosidade
// não é monótona (o azul #4098ef CLAREIA depois dos cinzas — L 0,587 → 0,667,
// então a linha "mais recente" parecia mais antiga) e os quatro cinzas ficam a
// ΔL 0,041–0,044, abaixo do piso 0,06 de degrau visível.
//
// Esta passa em tudo (monotonia, ΔL ≥ 0,06, ponta clara 2,13:1, matiz único).
// A distinção carga histórica × emissão MWA não se perdeu: ela é carregada
// pelo TRACEJADO e pelo selo na legenda, que é onde o handoff já a colocava —
// o matiz duplicava essa informação e custava a ordenação.
export const VINTAGE_COLORS = [
   '#93b3d0',
   '#7099c4',
   '#4d84be',
   '#2f6aa8',
   '#17538c',
   '#0b3a63',
];

// ─── Aging ────────────────────────────────────────────────────────────────
// Rótulos dos buckets que a API devolve SEMPRE, na mesma ordem, mesmo zerados.
export const AGING_BUCKET_LABELS = {
   '1-30': '1-30 dias',
   '31-60': '31-60 dias',
   '61-90': '61-90 dias',
   '90+': '90+ dias',
};

// Cores da barra de aging.
//
// "A vencer" é um ESTADO à parte (não é atraso) e leva o azul de ação do DS.
// Os 4 buckets vencidos são uma escala ORDINAL — faixas de idade, onde a ordem
// carrega significado —, então seguem uma rampa de UM matiz, clara → escura,
// terminando exatamente no token `danger` (#ab2020). A severidade se lê no
// escurecimento, não na troca de matiz.
//
// ⚠️ Diverge do handoff de design, que fixava #F2AF29 → #e08b1f → #c95c1a →
// #ab2020. Aquela rampa foi reprovada na verificação de paleta: os pares
// vizinhos ficam a ΔE 9,4 / 12,0 / 13,3 (piso 15 para visão normal) — ou seja,
// são difíceis de distinguir MESMO com visão de cores completa, e são
// justamente os segmentos que se encostam na barra empilhada. O vão total
// amarelo→vermelho é 34,7 e precisaria de ~45 para comportar 4 degraus legais,
// então re-espaçar dentro daquela família não resolvia.
//
// Esta rampa passa em todas as verificações (monotonia de luminosidade, degrau
// ΔL ≥ 0.06, ponta clara ≥ 2:1 sobre a superfície, matiz único).
export const AGING_COLORS = {
   a_vencer: '#4098ef',
   '1-30': '#e89578',
   '31-60': '#d4704f',
   '61-90': '#bd4526',
   '90+': '#ab2020',
};

// Desfecho dos títulos que JÁ venceram (dobra 2).
//
// Azul = dinheiro que entrou (dois tons: em dia × recuperado); quente = ainda
// não entrou. A separação por MATIZ é o que carrega a leitura — e é a única que
// sobrevive à daltonia: verde/salmão, o par "natural" para pago × não pago,
// colapsa sob protanopia (ΔE 0,7 medido; praticamente a mesma cor).
//
// A escolha de não usar vermelho aqui é deliberada e vem do dado: 96,8% de tudo
// que vence acaba pago (0084 §7.3). Pintar 45,7% de "adimplência em dia" com
// cor de alarme leria como perda, e perda não é o que este número diz.
export const OUTCOME_COLORS = {
   paidOnTime: '#1f66bd',
   recovered: '#5aa7e5',
   stillOpen: '#e89578',
};

// Tinta do rótulo DENTRO de cada faixa, escolhida por contraste medido (WCAG
// ≥ 4,5:1 para texto pequeno). Branco só nos dois degraus escuros; nos claros
// o branco cairia para 2,3:1 e o número ficaria ilegível.
export const AGING_LABEL_INK = {
   a_vencer: '#1f1f18', // branco daria 3,02:1
   '1-30': '#1f1f18', // branco daria 2,33:1
   '31-60': '#1f1f18', // branco daria 3,37:1
   '61-90': '#ffffff', // 5,19:1
   '90+': '#ffffff', // 7,10:1
};

// ─── Notas fixas da tela (complementam as `notes` da API) ─────────────────
//
// A API manda 3 notas sobre a coleta; estas 3 são ressalvas do MODELO de dados
// que o operador precisa alcançar para ler os números sem se enganar (0084 §1
// "o que este painel NÃO pode mostrar").
export const STATIC_QUALITY_NOTES = [
   'Não existe valor efetivamente pago, juros, multa, desconto no pagamento nem pagamento parcial — o modelo é binário: pago ou não.',
   'O DSO mede da emissão do boleto ao pagamento, não da venda: não há data de venda no sistema.',
   'O vencido é calculado por data e inclui títulos PENDING já vencidos cujo webhook ainda não chegou — por isso pode ficar ligeiramente acima do total com status OVERDUE.',
];
